/**
 * SuperLikeModal - Modal d'envoi de Super Like Doré
 * Permet d'envoyer un Super Like avec message personnalisé
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { api } from '../../../services/api';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';

const SUPER_LIKE_COST = 20;

export function SuperLikeModal({ isDarkMode, customerId, targetId, targetName, targetAvatar, onClose, onSuccess }) {
  const [message, setMessage] = useState('');
  const [myCrowns, setMyCrowns] = useState(0);
  const [sending, setSending] = useState(false);
  
  useEffect(() => {
    const fetchCrowns = async () => {
      try {
        const res = await api.getCrowns(customerId);
        setMyCrowns(res.crowns || 0);
      } catch (error) {
        console.error('Erreur récupération couronnes:', error);
      }
    };
    fetchCrowns();
  }, [customerId]);
  
  const sendSuperLike = async () => {
    if (sending) return;
    
    if (myCrowns < SUPER_LIKE_COST) {
      toast.error(`Pas assez de couronnes ! (${SUPER_LIKE_COST} 👑 requis)`);
      return;
    }
    
    setSending(true);
    try {
      const result = await api.sendSuperLike(customerId, targetId, message);
      
      // Animation confetti pour Super Like
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#FFD700', '#FFA500', '#FF69B4']
      });
      
      if (result.isMatch) {
        toast.success(`🎉 SUPER MATCH avec ${targetName} ! +10 couronnes bonus !`);
      } else {
        toast.success(`💎 Super Like envoyé à ${targetName} !`);
      }
      
      onSuccess?.(result);
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur envoi Super Like');
    } finally {
      setSending(false);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Header avec cible */}
      <div className="text-center">
        <div className="w-20 h-20 mx-auto mb-3 rounded-full bg-gradient-to-br from-yellow-400 via-orange-500 to-pink-500 flex items-center justify-center text-4xl shadow-lg ring-4 ring-yellow-300/50">
          {targetAvatar || '💎'}
        </div>
        <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Super Like pour {targetName}
        </h3>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Démarque-toi et montre ton intérêt !
        </p>
      </div>
      
      {/* Solde couronnes */}
      <div className={`flex justify-between items-center p-3 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-yellow-50'}`}>
        <div className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Ton solde
        </div>
        <div className="flex items-center gap-2 text-yellow-500 font-bold text-lg">
          👑 {myCrowns}
        </div>
      </div>
      
      {/* Coût */}
      <div className={`text-center p-4 rounded-xl border-2 border-dashed ${
        myCrowns >= SUPER_LIKE_COST 
          ? 'border-yellow-400 bg-yellow-500/10' 
          : 'border-red-400 bg-red-500/10'
      }`}>
        <div className="text-3xl mb-1">💎</div>
        <div className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Coût : {SUPER_LIKE_COST} 👑
        </div>
        {myCrowns < SUPER_LIKE_COST && (
          <div className="text-red-500 text-sm mt-1">
            Il te manque {SUPER_LIKE_COST - myCrowns} couronnes
          </div>
        )}
      </div>
      
      {/* Message personnalisé */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Message personnalisé (optionnel)
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Ex: J'ai adoré ton profil, tu as l'air super sympa ! 😊"
          maxLength={200}
          rows={3}
          className={`w-full p-3 rounded-xl resize-none ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' 
              : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
          } border focus:ring-2 focus:ring-yellow-500 focus:border-transparent`}
        />
        <div className={`text-xs text-right mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {message.length}/200
        </div>
      </div>
      
      {/* Avantages */}
      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          ✨ Avantages du Super Like :
        </div>
        <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          <li>• {targetName} verra ton profil en priorité</li>
          <li>• Ton message sera mis en avant 💬</li>
          <li>• +10 couronnes bonus si match ! 🎉</li>
        </ul>
      </div>
      
      {/* Boutons */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onClose}
          className="flex-1"
        >
          Annuler
        </Button>
        <Button
          onClick={sendSuperLike}
          disabled={sending || myCrowns < SUPER_LIKE_COST}
          className={`flex-1 ${
            myCrowns >= SUPER_LIKE_COST
              ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white'
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          {sending ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin">⏳</span> Envoi...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              💎 Envoyer ({SUPER_LIKE_COST} 👑)
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}

export default SuperLikeModal;
